<script lang="ts">
  /**
   * The tick tape's state pane — the RENDER half of the rung ladder (`tickState.ts`).
   *
   * ⭐ THE DESIGN DECISION THIS PANEL IS BUILT ON, AND IT IS DERIVED FROM LAW RATHER THAN TASTE:
   * **WHEN THE RUNG IS NOT `OK`, THE STATE *IS* THE CONTENT.** There is no chart underneath with a
   * badge on top. A series drawn in any non-OK rung would be a fabrication — a flat zero asserts
   * BALANCED FLOW, which is a trading signal the data never made — so the panel does not draw one
   * and then apologise. It says which rung it is on, and that is the whole panel.
   *
   * ⛔ CONSEQUENCE FOR ANYONE EXTENDING THIS: do not "improve" it by showing the last known series
   * behind a scrim. A dimmed real chart is still a chart, and the reader's eye takes the shape
   * before it takes the label.
   *
   * THREE VISUAL REGISTERS, NOT TWO — the panel distinguishes FAULT from EXPECTED from LIVE, because
   * the ladder's whole point is that `WAITING FOR FIRST TICK` (healthy and empty) and
   * `NO TICKS STORED` (broken and empty) look identical to every instrument except this one:
   *
   *   FAULT ...... amber, bordered, bold, glyph — someone must act
   *   EXPECTED ... quiet, bordered, muted — a fact about the world, not a fault
   *   LIVE ....... the series renders, and a zero delta is then a MEASUREMENT
   *
   * ⚠ EXPECTED IS DELIBERATELY QUIET, and that follows `StalenessChip`'s CLOSED precedent rather
   * than inventing a rule: *a signal that cries wolf on schedule is one people stop reading.* The
   * owner looks straight after a fix fires and sees `WAITING FOR FIRST TICK`; if that were amber,
   * a working deploy would read as a failed one on the only screen that matters.
   *
   * ⛔ LOUD BY CONSTRUCTION: the fault register changes COLOUR, BORDER, WEIGHT and adds a GLYPH.
   * Never colour alone — that vanishes for a colour-blind reader (the rule `StalenessChip` earned).
   *
   * ⭐ EVERY FIGURE GOES THROUGH `renderFigure`, which has no path that emits a bare number: the
   * as-of attaches to the NUMBER, not to the panel around it, because a screenshot carries the
   * figure and leaves the panel behind.
   */
  import { tickRung, type TickProbe } from "./tickState";
  import { toPriceRegister, cvdRegister, type TicksPayload } from "./tickApi";
  import { feedState } from "./session";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";

  interface Props {
    /** The current probe of the serve leg. null = we have not asked yet (not "empty"). */
    probe: TickProbe | null;
    /** The previous probe. Without it rung 7 is UNDECIDABLE and the panel says so. */
    prev?: TickProbe | null;
    /** Freshness budget for the chip, seconds. A different question from sufficiency. */
    budgetS?: number;
    /** Feed-level transport error, if the fetch itself failed. */
    error?: string | null;
    /**
     * The raw payload, for the registers that read fields the RUNG does not.
     * ⛔ The pane still grades its state from the PROBE. This is not a second source of truth; it is
     * the price and CVD registers reading their own fields, each with its own provenance.
     */
    payload?: TicksPayload | null;
    /** Injected for deterministic rendering in tests/screenshots. */
    nowMs?: number;
  }
  let { probe, prev = null, budgetS = 90, error = null, payload = null, nowMs = Date.now() }: Props = $props();
  let infoOpen = $state(false);

  // A probe we have not taken is NOT an unreachable endpoint and NOT an empty store. Until the
  // first fetch resolves there is no rung to name, and naming one would be inventing a measurement.
  /*
    THE PRICE REGISTER — the owner's "the price must feel live".
    ⛔ THE SLOW PRICE WAS NEVER A POLL RATE. The price he watches elsewhere is the CLOSE OF A 1-MINUTE
    BAR, which changes once a minute BY CONSTRUCTION — measured on both live routes, 8s apart, three
    times. This register renders the TICK-level last price, which moves with the tape and carries its
    OWN row's age.
  */
  let px = $derived(toPriceRegister(payload));
  /*
    ⛔⛔ SESSION AWARENESS — ADDED AFTER WATCHING THIS PANE CRY WOLF ON A SCHEDULED HALT.
    Observed on the owner's live surface at 21:1xZ, mid CME daily maintenance halt: the chip read
    `ticks STALE 11m 49s` and the price read `NOT CURRENT`, both AMBER, for a market that is closed
    BY TIMETABLE. `StalenessChip`'s own header says why that is worse than nothing:
      *"a signal that cries wolf on schedule is one people stop reading. That is fail-loud theatre."*
    ⇒ ***A HALT IS A FACT ABOUT THE WORLD, NOT A FAULT — and the price register had no concept of one.***
    The session verdict comes from `session.ts`, the SAME source the chip uses, so the two can never
    disagree about whether the market is open.
  */
  let lastBarMs = $derived(probe?.newestRowMs ?? null);
  let pxSession = $derived(
    // ⛔ barSpanMs is 0 BECAUSE A TICK IS AN INSTANT, NOT A BUCKET — `newestRowMs` is a trade's
    // own timestamp, so it needs no widening to reach the close. This is the honest value, not a
    // default: R497(2) made the argument REQUIRED precisely so this pane could not silently
    // inherit a chart rung's span, which would excuse a real outage by up to a whole bucket.
    feedState({ ageS: px.ageS, budgetS, lastBarMs, barSpanMs: 0, now: nowMs }),
  );
  let pxClosed = $derived(pxSession.state === "closed" || pxSession.state === "reopening");
  let cvd = $derived(cvdRegister(payload));

  let verdict = $derived(
    probe === null
      ? null
      : tickRung(probe, prev),
  );

  /**
   * ⛔ THE CHIP'S AGE COMES FROM THE DATA (`newestRowMs`), NEVER FROM THE PROBE (`asOfMs`).
   * Feeding it `asOfMs` renders a green chip whenever WE are running, which is always — it measured
   * when we asked rather than how fresh the answer was, and it shipped a green `ticks 8s` beside a
   * `NO FEED` body until a render caught it. `null` here means "no row to be fresh about", and the
   * chip's own NO DATA branch is the correct render for that.
   */
  let ageS = $derived(
    probe === null || probe.newestRowMs === null
      ? null
      : Math.max(0, Math.round((nowMs - probe.newestRowMs) / 1000)),
  );

  let stateLabel = $derived(
    pxClosed ? "CLOSED" : error !== null || pxSession.state === "stale"
      ? "STALE"
      : verdict?.canRenderSeries ? "LIVE" : "UNDECIDED",
  );
  let freshnessStale = $derived(error !== null || pxSession.state === "stale");
  let freshnessTitle = $derived(
    ageS === null ? "ticks age unserved" : `ticks age ${ageS}s · budget ${budgetS}s`,
  );
</script>

<section class="card">
  <header class="panel-id">
    <h2>ES · tick tape</h2>
    <span class="pill">5m</span>
    <PanelFreshness label="ticks" stale={freshnessStale} ageTitle={freshnessTitle} />
    <PanelInfo id="tick-info" label="Tick tape information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>{verdict?.detail ?? "A series can be drawn. ⚠ Rung 7 UNDECIDED (rows present but not advancing) — it needs a second probe at least 60s later over the SAME window and population."}</p>
      <p>source spx_raw.tick_databento_v1 · population {probe?.population ?? "—"}</p>
      {#if probe}
        <dl class="facts">
          <div><dt>rows in window</dt><dd>{probe.rowsInWindow ?? "—"} · window {probe.windowS}s · as of {ageS === null ? "—" : `${ageS}s ago`}</dd></div>
          <div><dt>watermark</dt><dd>{probe.watermark === null ? "—" : probe.watermark} · as of {Math.max(0, Math.round((nowMs - probe.asOfMs) / 1000))}s ago</dd></div>
        </dl>
      {/if}
      {#if error}<p>feed error · {error}</p>{/if}
    </PanelInfo>
  </header>

  <!--
    The price sits ABOVE the rung, because it is the thing being watched and the rung is the thing
    qualifying it. ⛔ Its age rides the NUMBER, not the panel: a fast number that silently freezes is
    worse than a slow one that admits its age.
  -->
  {#if px.price !== null}
    <div class="px" class:frozen={!px.fresh && !pxClosed} class:closed={pxClosed}>
      <span class="pxv">{px.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
      <span class="pxa">{px.ageS === null ? "age unreported" : `${px.ageS}s`}</span>
      {#if !px.fresh}
        {#if pxClosed}
          <!-- Quiet on purpose: amber here is the false alarm this branch exists to remove. -->
          <span class="pxc">{pxSession.session.label} — last trade before the close</span>
        {:else}
          <span class="pxw">NOT CURRENT</span>
        {/if}
      {/if}
    </div>
  {/if}

  <div class="body">
    <!--
      THE CVD REGISTER — three states, and the distinction is the whole object. An ABSENT CVD and a
      ZERO CVD are different facts and only one of them is a trading signal; a flat line at zero
      asserts balanced flow the data never claimed.
    -->
    <div class="cvd">
      <span class="cvdl">CVD</span>
      {#if cvd.state === "ABSENT"}
        <span class="cvda">—</span>
      {:else}
        <span class="cvdv">{cvd.value!.toLocaleString()} contracts</span>
      {/if}
    </div>

    <div class="state-pill" role={stateLabel === "STALE" ? "alert" : "status"}>{stateLabel}</div>
  </div>
</section>

<style>
  .card {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow);
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .hd {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--line);
    flex-wrap: wrap;
  }
  h2 {
    margin: 0;
    font: 600 13px/1.2 var(--font-ui);
    color: var(--ink);
    letter-spacing: 0.01em;
  }
  .sub {
    font: 400 10px/1.3 var(--font-num);
    color: var(--faint);
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .body {
    padding: 12px;
  }

  .px {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    flex-wrap: wrap;
  }
  .pxv {
    font: 700 22px/1.1 var(--font-num);
    color: var(--ink);
    letter-spacing: -0.01em;
  }
  .pxa {
    font: 400 11px/1.2 var(--font-num);
    color: var(--muted);
  }
  /* Frozen is LOUD on three channels, never colour alone. */
  /* Frozen is amber ONLY when the market is open; a scheduled halt is muted, never alarming. */
  .px.frozen .pxv {
    color: var(--stale-ink);
  }
  .px.closed .pxv {
    color: var(--muted);
  }
  .pxc {
    font: 600 10px/1.2 var(--font-num);
    letter-spacing: 0.06em;
    color: var(--muted);
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: var(--radius-chip);
    padding: 1px 6px;
  }
  .pxw {
    font: 700 10px/1.2 var(--font-num);
    letter-spacing: 0.08em;
    color: var(--stale-ink);
    background: var(--stale-soft);
    border: 1px solid var(--stale);
    border-radius: var(--radius-chip);
    padding: 1px 6px;
  }
  .pxs {
    font: 400 10px/1.3 var(--font-ui);
    color: var(--faint);
    margin-left: auto;
  }
  .cvd {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 10px;
    font: 400 11px/1.4 var(--font-num);
    flex-wrap: wrap;
  }
  .cvdl {
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--cvd);
  }
  .cvdv {
    color: var(--ink);
    font-weight: 700;
  }
  .cvda {
    color: var(--faint);
  }

  /* The state block. It is the panel's content, so it is sized like content and not like a badge. */
  .state,
  .live {
    display: flex;
    flex-direction: column;
    gap: 6px;
    border-radius: var(--radius-card);
    padding: 12px;
    border: 1px solid var(--line);
  }
  .rung {
    font: 700 15px/1.2 var(--font-ui);
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .glyph {
    font-size: 11px;
  }
  .detail {
    margin: 0;
    font: 400 12px/1.5 var(--font-ui);
    color: var(--muted);
  }

  /*
    FAULT — loud on four channels at once: colour, background, border AND weight, plus the glyph.
    --stale-ink, NOT --stale: the palette receipt measured --stale on --stale-soft at 3.14:1, which
    fails AA for small text. --stale stays the border colour. (Same finding StalenessChip records.)
  */
  .state.fault {
    background: var(--stale-soft);
    border-color: var(--stale);
  }
  .state.fault .rung {
    color: var(--stale-ink);
  }

  /*
    EXPECTED — deliberately quiet. Bordered and labelled so it can never be mistaken for LIVE, but
    never amber: this register carries WAITING FOR FIRST TICK, which is a HEALTHY state, and an
    alarm here would make a working deploy read as a failed one.
  */
  .state.expected {
    background: var(--surface-2);
  }
  .state.expected .rung {
    color: var(--muted);
  }

  .live {
    background: var(--surface-2);
  }
  .rung.ok {
    color: var(--cvd);
  }

  /* Facts: the qualifier stack. A count without its predicate is a coordinate without an origin. */
  .facts {
    margin: 4px 0 0;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 10px;
    font: 400 11px/1.5 var(--font-num);
  }
  .facts > div {
    display: contents;
  }
  dt {
    color: var(--faint);
    white-space: nowrap;
  }
  dd {
    margin: 0;
    color: var(--ink);
    overflow-wrap: anywhere;
  }

  /* Mobile: the qualifier stack goes single-column rather than squeezing the values. */
  @media (max-width: 420px) {
    .facts {
      grid-template-columns: 1fr;
      gap: 0 0;
    }
    dt {
      margin-top: 4px;
    }
    .rung {
      font-size: 14px;
    }
  }
</style>
